import { useRef, useEffect, useState } from 'react';
import {
    Box,
    Flex,
    IconButton,
    Input,
    Text,
    VStack,
    Spinner,
    useToast,
    Collapse
} from '@chakra-ui/react';
import { FaTimes, FaPaperPlane, FaRobot, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { sendChatMessage } from '../../services/aiChatService';
import { useAIChat } from '../../context/AIChatContext';

const AIChatPanel = ({
    graph,
    econVars,
    onRunSimulation,
    onOpenEconomics,
    onAddEquipment,
    onBuildFlowsheet
}) => {
    const {
        isOpen,
        close,
        isCollapsed,
        setIsCollapsed,
        messages,
        setMessages
    } = useAIChat();

    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const messagesEndRef = useRef(null);
    const toast = useToast();

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    // Build full context from diagram (local AI - includes all data)
    const buildDiagramContext = () => {
        if (!graph) return null;

        const cells = graph.getCells();
        const links = graph.getLinks();

        const equipment = cells
            .filter(cell => cell.attributes && cell.attributes.isUnit === 'yes')
            .map(cell => {
                const attrs = cell.attributes;
                const label = attrs.attrs?.shapename?.innerHTML?.replace(/<\/?[^>]+>/g, '') ||
                              attrs.attrs?.label?.text ||
                              'Unnamed';
                return {
                    id: cell.id,
                    type: attrs.unitType || attrs.type,
                    label: label,
                    properties: attrs.equipment_Economics || {},
                    unitProperties: attrs.unitProperties || {}
                };
            });

        const streams = links.map(link => {
            const attrs = link.attributes;
            return {
                id: link.id,
                name: attrs.CustomName || attrs.stream_Number || 'Unnamed Stream',
                from: link.get('source')?.id,
                to: link.get('target')?.id,
                streamProperties: attrs.specified_Stream_Properties || {},
                solvedProperties: attrs.solved_Stream_Properties || {}
            };
        });

        // Include economic variables if available (pass entire econVars object)
        const economics = econVars || null;

        return {
            equipmentCount: equipment.length,
            streamCount: streams.length,
            equipment,
            streams,
            economics
        };
    };

    const handleSend = async () => {
        if (!input.trim() || isLoading) return;

        const userMessage = {
            id: Date.now().toString(),
            role: 'user',
            content: input
        };

        setMessages(prev => [...prev, userMessage]);
        setInput('');
        setIsLoading(true);

        try {
            const diagramContext = buildDiagramContext();

            const response = await sendChatMessage(
                input,
                messages,
                diagramContext
            );

            const assistantMessage = {
                id: (Date.now() + 1).toString(),
                role: 'assistant',
                content: response.message
            };

            setMessages(prev => [...prev, assistantMessage]);

            // Handle any actions the AI suggested
            if (response.action) {
                handleAction(response.action);
            }

        } catch (error) {
            console.error('Chat error:', error);
            toast({
                title: 'Connection Error',
                description: 'Could not connect to AI. Make sure Ollama is running (ollama serve).',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });

            // Add error message
            setMessages(prev => [...prev, {
                id: (Date.now() + 1).toString(),
                role: 'assistant',
                content: 'Sorry, I couldn\'t connect to the AI service. Please make sure Ollama is running locally.'
            }]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleAction = (action) => {
        switch (action.type) {
            case 'run_simulation':
                if (onRunSimulation) {
                    onRunSimulation();
                    toast({
                        title: 'Simulation Started',
                        status: 'info',
                        duration: 2000,
                    });
                }
                break;
            case 'open_economics':
                if (onOpenEconomics) {
                    onOpenEconomics();
                }
                break;
            case 'add_equipment':
                if (onAddEquipment && action.equipmentType) {
                    onAddEquipment(action.equipmentType);
                    toast({
                        title: 'Equipment Added',
                        description: `Added ${action.equipmentType} to diagram`,
                        status: 'success',
                        duration: 2000,
                    });
                }
                break;
            case 'build_flowsheet':
                if (onBuildFlowsheet && action.flowsheetType) {
                    onBuildFlowsheet(action.flowsheetType);
                    toast({
                        title: 'Building flowsheet...',
                        status: 'info',
                        duration: 3000,
                    });
                }
                break;
            case 'build_template':
                if (onBuildFlowsheet && action.template) {
                    onBuildFlowsheet(action.template);
                    toast({
                        title: 'Building flowsheet...',
                        status: 'info',
                        duration: 3000,
                    });
                }
                break;
            default:
                break;
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    if (!isOpen) return null;

    return (
        <Box
            position="fixed"
            right="20px"
            top="80px"
            width="380px"
            bg="white"
            borderRadius="12px"
            boxShadow="0 4px 20px rgba(0,0,0,0.15)"
            display="flex"
            flexDirection="column"
            zIndex={1000}
            border="1px solid"
            borderColor="gray.200"
            maxHeight={isCollapsed ? "auto" : "calc(100vh - 120px)"}
            transition="all 0.2s ease-in-out"
        >
            {/* Header */}
            <Flex
                p={3}
                borderBottom={isCollapsed ? "none" : "1px solid"}
                borderColor="gray.200"
                align="center"
                justify="space-between"
                bg="blue.500"
                borderRadius={isCollapsed ? "12px" : "12px 12px 0 0"}
                cursor="pointer"
                onClick={() => setIsCollapsed(!isCollapsed)}
                _hover={{ bg: 'blue.600' }}
                transition="all 0.2s"
            >
                <Flex align="center" gap={2}>
                    <FaRobot color="white" />
                    <Text fontWeight="bold" color="white">AI Assistant</Text>
                    {isCollapsed && messages.length > 1 && (
                        <Box
                            bg="white"
                            color="blue.500"
                            borderRadius="full"
                            px={2}
                            fontSize="xs"
                            fontWeight="bold"
                        >
                            {messages.length - 1}
                        </Box>
                    )}
                </Flex>
                <Flex gap={1}>
                    <IconButton
                        aria-label="Toggle"
                        icon={isCollapsed ? <FaChevronUp /> : <FaChevronDown />}
                        size="sm"
                        variant="ghost"
                        color="white"
                        _hover={{ bg: 'blue.700' }}
                        onClick={(e) => {
                            e.stopPropagation();
                            setIsCollapsed(!isCollapsed);
                        }}
                    />
                    <IconButton
                        aria-label="Close"
                        icon={<FaTimes />}
                        size="sm"
                        variant="ghost"
                        color="white"
                        _hover={{ bg: 'blue.700' }}
                        onClick={(e) => {
                            e.stopPropagation();
                            close();
                        }}
                    />
                </Flex>
            </Flex>

            <Collapse in={!isCollapsed} animateOpacity>
                {/* Messages */}
                <VStack
                    height="400px"
                    overflowY="auto"
                    p={3}
                    spacing={3}
                    align="stretch"
                    bg="gray.50"
                >
                    {messages.map((msg) => (
                        <Box
                            key={msg.id}
                            alignSelf={msg.role === 'user' ? 'flex-end' : 'flex-start'}
                            bg={msg.role === 'user' ? 'blue.500' : 'white'}
                            color={msg.role === 'user' ? 'white' : 'gray.800'}
                            px={4}
                            py={2}
                            borderRadius="lg"
                            maxW="85%"
                            boxShadow="sm"
                            border={msg.role === 'assistant' ? '1px solid' : 'none'}
                            borderColor="gray.200"
                        >
                            <Text fontSize="sm" whiteSpace="pre-wrap">{msg.content}</Text>
                        </Box>
                    ))}

                    {isLoading && (
                        <Flex alignSelf="flex-start" align="center" gap={2} p={2}>
                            <Spinner size="sm" color="blue.500" />
                            <Text fontSize="sm" color="gray.500">Thinking...</Text>
                        </Flex>
                    )}

                    <div ref={messagesEndRef} />
                </VStack>

                {/* Input */}
                <Flex p={3} borderTop="1px solid" borderColor="gray.200" gap={2} bg="white" borderBottomRadius="12px">
                    <Input
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="Ask about your diagram..."
                        disabled={isLoading}
                        bg="gray.50"
                        _focus={{ bg: 'white', borderColor: 'blue.500' }}
                    />
                    <IconButton
                        aria-label="Send"
                        icon={<FaPaperPlane />}
                        colorScheme="blue"
                        onClick={handleSend}
                        isLoading={isLoading}
                    />
                </Flex>
            </Collapse>
        </Box>
    );
};

export default AIChatPanel;
